import React, { useState, useEffect, useCallback } from 'react';
import { Button } from '../components/ui/button';
import { Mail, Phone, MapPin } from 'lucide-react';
import { Hero } from '../components/landing/Hero';
import { TrustRow } from '../components/landing/TrustRow';
import { StatStrip } from '../components/landing/StatStrip';
import { HowItWorks } from '../components/landing/HowItWorks';
import { TheMoment } from '../components/landing/TheMoment';
import { GuessingGame } from '../components/landing/GuessingGame';
import { MessageWriter } from '../components/landing/MessageWriter';
import { Occasions } from '../components/landing/Occasions';
import { Faq } from '../components/landing/Faq';
import { PartnerCta } from '../components/landing/PartnerCta';
import { FinalCta } from '../components/landing/FinalCta';
import { DemoModal } from '../components/landing/DemoModal';
import { VideoModal } from '../components/landing/VideoModal';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const Header = ({ onJoinClick }) => (
  <header className="sticky top-0 z-50 border-b border-border/60 bg-background/80 backdrop-blur-md">
    <div className="mx-auto flex max-w-7xl items-center justify-between px-6 py-4 md:px-12">
      <div className="flex items-center gap-2.5">
        <img src="/unwp-logo.svg" alt="UNWP" className="h-9 w-9" />
        <div className="leading-tight">
          <span className="font-display text-xl font-bold tracking-tight">UNWP <span className="text-sm font-semibold text-primary">(un-wrap)</span></span>
          <p className="text-[11px] text-muted-foreground">Where stories become gifts</p>
        </div>
      </div>
      <nav className="hidden items-center gap-8 md:flex">
        <a href="#how" className="text-sm font-medium text-muted-foreground transition-colors hover:text-foreground" data-testid="nav-how">How it works</a>
        <a href="#try-ai" className="text-sm font-medium text-muted-foreground transition-colors hover:text-foreground" data-testid="nav-try-ai">AI Writer</a>
        <a href="#occasions" className="text-sm font-medium text-muted-foreground transition-colors hover:text-foreground" data-testid="nav-occasions">Occasions</a>
        <a href="#faq" className="text-sm font-medium text-muted-foreground transition-colors hover:text-foreground" data-testid="nav-faq">FAQ</a>
      </nav>
      <Button onClick={onJoinClick} className="rounded-full bg-primary px-5 font-semibold text-primary-foreground hover:bg-primary/90" data-testid="header-join-button">
        Join the waitlist
      </Button>
    </div>
  </header>
);

const Footer = () => (
  <footer className="bg-foreground/95 py-14 text-background">
    <div className="mx-auto grid max-w-7xl gap-10 px-6 md:grid-cols-3 md:px-12">
      <div>
        <div className="mb-4 flex items-center gap-2.5">
          <img src="/unwp-logo.svg" alt="UNWP" className="h-9 w-9 brightness-0 invert" />
          <span className="font-display text-xl font-bold">UNWP</span>
        </div>
        <p className="max-w-xs text-sm leading-relaxed text-background/60">
          The experience layer for every surprise — with or without a gift card. Where stories become gifts.
        </p>
      </div>
      <div>
        <h4 className="mb-4 font-display text-sm font-semibold uppercase tracking-wider text-background/80">Explore</h4>
        <ul className="space-y-2.5 text-sm text-background/60">
          <li><a href="#how" className="transition-colors hover:text-background">How it works</a></li>
          <li><a href="#occasions" className="transition-colors hover:text-background">Occasions</a></li>
          <li><a href="#faq" className="transition-colors hover:text-background">FAQ</a></li>
        </ul>
      </div>
      <div>
        <h4 className="mb-4 font-display text-sm font-semibold uppercase tracking-wider text-background/80">Get in touch</h4>
        <ul className="space-y-2.5 text-sm text-background/60">
          <li className="flex items-center gap-2.5"><Mail className="h-4 w-4" /><a href="mailto:Shreneve@unwp.co.uk" className="transition-colors hover:text-background">Shreneve@unwp.co.uk</a></li>
          <li className="flex items-center gap-2.5"><Phone className="h-4 w-4" /><a href="tel:+447553134407" className="transition-colors hover:text-background">+44 7553 134407</a></li>
          <li className="flex items-center gap-2.5"><MapPin className="h-4 w-4" /> London, United Kingdom</li>
        </ul>
      </div>
    </div>
    <div className="mx-auto mt-12 max-w-7xl border-t border-background/10 px-6 pt-6 text-center md:px-12">
      <p className="text-xs text-background/50">© 2025 UNWP. All rights reserved.</p>
    </div>
  </footer>
);

const LandingPage = () => {
  const [signupCount, setSignupCount] = useState(0);
  const [showDemo, setShowDemo] = useState(false);
  const [showVideo, setShowVideo] = useState(false);

  useEffect(() => {
    fetch(`${BACKEND_URL}/api/waitlist/count`)
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => data && setSignupCount(data.count))
      .catch(() => {});
  }, []);

  const handleJoined = useCallback((data) => {
    if (data?.new_signup !== false) setSignupCount((c) => c + 1);
  }, []);

  const scrollToCta = useCallback(() => {
    document.getElementById('join')?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  return (
    <div className="min-h-screen bg-background text-foreground antialiased">
      <Header onJoinClick={scrollToCta} />
      <Hero signupCount={signupCount} onJoined={handleJoined} onOpenVideo={() => setShowVideo(true)} />
      <TrustRow />
      <TheMoment />
      <StatStrip />
      <HowItWorks onOpenDemo={() => setShowDemo(true)} />
      <GuessingGame />
      <MessageWriter />
      <Occasions />
      <Faq />
      <PartnerCta />
      <FinalCta onJoined={handleJoined} />
      <Footer />
      <DemoModal
        open={showDemo}
        onOpenChange={setShowDemo}
        onJoin={() => {
          setShowDemo(false);
          scrollToCta();
        }}
      />
      <VideoModal open={showVideo} onOpenChange={setShowVideo} />
    </div>
  );
};

export default LandingPage;
